/**
 * ============================================================================
 * ReviewMinutes — data layer.
 * Owner: Phase 7 — Management Review Module (Agent F).
 * Purpose: React-Query hook backing the ReviewMinutes screen. Hits
 *          GET /api/v1/management-reviews/{id} and exposes the KPI snapshot
 *          + action items + history.
 * URS rows: MGR-001..008.
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { buildHeaders, apiUrl } from '../../api/client';
import type { ReviewListItem } from '../ReviewCalendar/useReviewCalendarQuery';

export interface KpiSnapshot {
  snapshotId: string;
  capturedAtUtc: string;
  capaOpenCount: number;
  capaOverdueCount: number;
  deviationCriticalCount: number;
  deviationMajorCount: number;
  oosPhase2OpenCount: number;
  supplierDisqualifiedCount: number;
  equipmentRestrictedCount: number;
  trainingOverdueCount: number;
  auditFindingsOpenCount: number;
  riskCriticalCount: number;
  otherMetrics?: Record<string, unknown>;
}

export interface ActionItem {
  id: string;
  reviewId: string;
  actionNumber: string;
  title: string;
  description: string;
  ownerUserId: string;
  dueUtc: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'CLOSED' | 'CANCELLED';
  createsCapa: boolean;
  capaId?: string | null;
  completedAtUtc?: string | null;
}

export interface ReviewHistoryEntry {
  id: string;
  reviewId: string;
  eventCode: string;
  eventAtUtc: string;
  actorUserId?: string | null;
  payload?: Record<string, unknown>;
}

export interface ReviewDetail extends ReviewListItem {
  tenantId: string;
  attendeeUserIds: string[];
  attendeeCategoriesPresent: string[];
  minutesDocId?: string | null;
  minutesSignedAtUtc?: string | null;
  minutesSignerUserId?: string | null;
  kpiSnapshot?: KpiSnapshot | null;
  actionItems: ActionItem[];
  history: ReviewHistoryEntry[];
}

export function useReviewDetailQuery(reviewId: string | undefined) {
  return useQuery<ReviewDetail>({
    queryKey: ['management-reviews', 'detail', reviewId ?? ''],
    enabled: !!reviewId,
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/v1/management-reviews/${reviewId}`), { headers: buildHeaders() });
      if (!res.ok) throw new Error(`management-reviews/${reviewId} ${res.status}`);
      return res.json();
    },
  });
}
